import Checkbox from "@mui/material/Checkbox";
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import React, { useState } from 'react';
import { FormControlLabel, Grid, TextField as MuiTextField, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { TakePaymentFields } from './take-payment-fields';
import { RootState } from '../../state/root-reducer';
import { CrossoverSelectionEnum } from "@finpay/estimation-types";

interface TakePaymentViewProps {
  formik: any;
  handleStripeStatus: (status: boolean) => void;
}

export function TakePaymentView(props: TakePaymentViewProps) {
  const {
    formik,
    handleStripeStatus
  } = props;

  const [displayPFRTotalError, setDisplayPFRTotalError] = useState(false);

  const estimate = useSelector((state: RootState) =>
    state.estimatorContext.selectedEstimate
  )

  const selectedPatient = useSelector((state: RootState) => 
    state.patientContext.selectedPatient
  )

  function totalEstimatedPFR() {
      const pfrSummary: any = estimate?.isPlanYearCrossover
          ? estimate.crossOverSummary
          : estimate?.summary;
      const pfrAmt = estimate?.isPlanYearCrossover
          ? estimate?.crossoverSelection === CrossoverSelectionEnum.BEFORE
              ? estimate?.crossOverSummary?.summaryBeforeCrossover?.totalPFR
              : estimate?.crossOverSummary?.summaryWithCrossover?.totalPFR
          : pfrSummary?.totalPFR;
      return pfrAmt ?? 0;
  }
  
  const handleAmountChange = (e: any, fieldName: string) => {
    const { value } = e.target;
    
    const maxAmount: number = totalEstimatedPFR();
    
    // Handle empty or invalid values properly
    let numericValue = 0;
    if (value !== '' && !isNaN(parseFloat(value))) {
      numericValue = Math.min(parseFloat(value), maxAmount);
    }
    
    const pfrAmt = totalEstimatedPFR();
    
    if (fieldName === 'payNowAmt') {
      formik.setFieldValue('payNowAmt', value === '' ? '' : numericValue.toString());
    } else if (fieldName === 'payAtFacilityAmt') {
      formik.setFieldValue('payAtFacilityAmt', value === '' ? '' : numericValue.toString());
    }

    // Use the actual numeric values for validation, not the display values
    const payNowAmt = fieldName === 'payNowAmt' ? numericValue : Number(formik.values.payNowAmt) || 0;
    const payAtFacilityAmt = fieldName === 'payAtFacilityAmt' ? numericValue : Number(formik.values.payAtFacilityAmt) || 0;

    if((pfrAmt - payAtFacilityAmt - payNowAmt) < 0){
      setDisplayPFRTotalError(true);
    } else {
      setDisplayPFRTotalError(false);
    }
  };

  return (
    <>
      <Typography variant="subtitle2" className="my-3" style={{marginTop: '22px'}}>
        Financial Clearance Details
      </Typography>
      <Grid container>
        <Grid item direction="column" md={4} sm={4} >
          <FormControlLabel control={
            <Checkbox
              checked={formik.values.isPayNow}
              name="isPayNow"
              onChange={(e)=>{
                formik.handleChange(e)
                // Don't clear the amount when toggling checkbox
                // formik.setFieldValue("payNowAmt", "");
              }}/>}
                label="Take Payment Now ?"
                className="label-chbx"
                labelPlacement="start"
                style={{marginLeft:0, display: 'flex'}} 
                test-id="complete-patient-record-take-payment-now-checkbox"
          />
        </Grid>
        <Grid item direction="column" md={8} sm={8} >
          <FormControlLabel
            control={
              <MuiTextField
                type="number"
                name="payNowAmt"
                value={formik.values.payNowAmt}
                onChange={(e) => {
                  handleAmountChange(e, 'payNowAmt');
                }}
                onBlur={formik.handleBlur}
                InputProps={{
                  startAdornment: <AttachMoneyIcon style={{color: '#afbecd'}}/>
                }}
                InputLabelProps={{
                  shrink: true
                }}
                disabled={!formik.values.isPayNow}
                test-id="complete-patient-record-take-payment-modal-amount-input"
              />
            } label={
              <Typography sx={{width: 7/10, paddingLeft: 2, textAlign: 'right', paddingRight: '30px' }}>
                Amount
              </Typography>
            } labelPlacement="start"  style={{marginLeft:20, display: 'flex', marginRight: '5px'}}
          />
        </Grid>
      </Grid>
      <Grid container style={{marginTop: '6px'}}>
        <Grid item direction="column" md={4} sm={4} >
          <FormControlLabel control={
            <Checkbox
              checked={formik.values.isPayAtFacility}
              name="isPayAtFacility"
              onChange={(e)=>{
                formik.handleChange(e);
                // Don't clear the amount when toggling checkbox
                // formik.setFieldValue("payAtFacilityAmt", "");
              }}/>}
              label="Take Payment at Facility ?"
              className="label-chbx"
              labelPlacement="start"
              style={{marginLeft:0, display: 'flex'}} 
            />
        </Grid>
        <Grid item direction="column" md={8} sm={8} >
          <FormControlLabel control={
            <MuiTextField
                name="payAtFacilityAmt"
                type="number"
                value={formik.values.payAtFacilityAmt}
                onChange={(e) => {
                  handleAmountChange(e, 'payAtFacilityAmt')
                }}
                onBlur={formik.handleBlur}
                InputProps={{
                  startAdornment: <AttachMoneyIcon style={{color: '#afbecd'}}/>
                }}
                InputLabelProps={{
                  shrink: true
                }}
                disabled={!formik.values.isPayAtFacility}
            />} label={
            <Typography sx={{width: 7/10, paddingLeft: 2, textAlign: 'right', paddingRight: '30px' }}>
              Amount
            </Typography>
          } labelPlacement="start"  style={{marginLeft:20, display: 'flex', marginRight: '5px'}}/>
        </Grid>
      </Grid>
      <Typography sx={{width: '100%', paddingTop: '8px', textAlign: 'right', fontSize: '13px', fontStyle: 'italic', color: displayPFRTotalError ? 'red' : 'black'}}>
        Total payment amount cannot exceed pfr amount
      </Typography>
      { formik.values.isPayNow && 
        <>
          <Grid container style={{marginTop: '12px'}}>
            <Grid item direction="column" md={12} sm={12}>
                              <MuiTextField
                  required
                  fullWidth
                  name="receiptEmail"
                  label="Receipt Email"
                  type="email"
                  value={formik.values.receiptEmail || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.receiptEmail && Boolean(formik.errors.receiptEmail)}
                  helperText={formik.touched.receiptEmail && formik.errors.receiptEmail}
                  test-id="complete-patient-record-receipt-email-input"
                />
            </Grid>
          </Grid>
          <Grid container style={{marginTop: '6px'}} >
            <Typography variant="subtitle2" className="my-3" style={{marginTop: '22px'}}>
              Payment Details
            </Typography>
            <TakePaymentFields 
              paymentMethodsFailed={false}
              requiresExistingSource={false}
              requiresRecurringPayment={false}
              formik={formik}
              stripeStatus={handleStripeStatus}
            />
          </Grid>
        </>
      }
    </>
  );
}
